import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DashboardService } from './dashboard.service';
import { DashboardIndicadores, IndicadorInfo } from './dashboard.model';
import { catchErr } from '../shared/utils';
import { interval, Subscription } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
    loading = false;
    dashboardData: DashboardIndicadores | null = null;
    today = new Date();
    
    indicadoresMensais: IndicadorInfo[] = [];
    
    tempoRestante = 300; // 5 minutos em segundos
    private updateSubscription?: Subscription;
    private timerSubscription?: Subscription;

    constructor(
        private dashboardService: DashboardService,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        console.log('DashboardComponent initialized');
        this.iniciarAtualizacaoAutomatica();
    }

    ngOnDestroy(): void {
        if (this.updateSubscription) {
            this.updateSubscription.unsubscribe();
        }
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
    }

    private iniciarAtualizacaoAutomatica(): void {
        // Atualizar dados a cada 5 minutos (300000 ms)
        this.updateSubscription = interval(300000)
            .pipe(
                startWith(0),
                switchMap(() => {
                    this.tempoRestante = 300;
                    return this.dashboardService.getIndicadores();
                })
            )
            .subscribe({
                next: (data) => {
                    this.dashboardData = data;
                    this.prepararIndicadores();
                    console.log('Dashboard data updated:', data);
                },
                error: (err) => catchErr(err, this.snackBar)
            });

        // Timer regressivo
        this.timerSubscription = interval(1000).subscribe(() => {
            if (this.tempoRestante > 0) {
                this.tempoRestante--;
            }
        });
    }

    private prepararIndicadores(): void {
        if (!this.dashboardData) return;

        this.indicadoresMensais = [
            {
                titulo: 'Criados',
                valor: this.dashboardData.criados,
                icone: 'add_circle',
                cor: 'primary',
                descricao: 'Quantidade total de DIOs criados no mês vigente (do dia 01 ao dia 31)',
                periodicidade: 'Mensal'
            },
            {
                titulo: 'Executados',
                valor: this.dashboardData.executados,
                icone: 'check_circle',
                cor: 'success',
                descricao: 'Quantidade de DIOs executados pelos condutores no mês vigente',
                periodicidade: 'Mensal'
            },
            {
                titulo: 'Aguardando Execução',
                valor: this.dashboardData.naoExecutados,
                icone: 'cancel',
                cor: 'warn',
                descricao: 'Quantidade de DIOs não executados pelos condutores no mês vigente',
                periodicidade: 'Mensal'
            },
            {
                titulo: 'Executor',
                valor: this.dashboardData.executor,
                icone: 'pending_actions',
                cor: 'accent',
                descricao: 'Quantidade de DIOs pendentes de atesto do Funcionário no mês vigente',
                periodicidade: 'Mensal'
            },
            {
                titulo: 'Aferição',
                valor: this.dashboardData.afericao,
                icone: 'assessment',
                cor: 'primary',
                descricao: 'Quantidade de DIOs pendentes de aferição no mês vigente',
                periodicidade: 'Mensal'
            },
            {
                titulo: 'Fiscal',
                valor: this.dashboardData.fiscal,
                icone: 'gavel',
                cor: 'accent',
                descricao: 'Quantidade de DIOs pendentes de validação do Fiscal no mês vigente',
                periodicidade: 'Mensal'
            },
        ];


        // indicadores diários agora são responsabilidade do componente DashboardIndicadoresDiarios
    }

    getCorClasse(cor: string): string {
        const cores: { [key: string]: string } = {
            'primary': 'text-primary',
            'success': 'text-success',
            'warn': 'text-danger',
            'accent': 'text-warning'
        };
        return cores[cor] || 'text-primary';
    }

    formatarTempoRestante(): string {
        const minutos = Math.floor(this.tempoRestante / 60);
        const segundos = this.tempoRestante % 60;
        return `${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`;
    }

    atualizarManualmente(): void {
        this.loading = true;
        this.dashboardService.getIndicadores().subscribe({
            next: (data) => {
                this.dashboardData = data;
                this.prepararIndicadores();
                this.tempoRestante = 300;
                this.snackBar.open('Indicadores atualizados!', '✓', { 
                    duration: 3000,
                    panelClass: ['success'] 
                });
            },
            error: (err) => catchErr(err, this.snackBar)
        }).add(() => this.loading = false);
    }
}
